import React from "react";
import { createPortal } from "react-dom";

const TOOLTIP_OPEN_DELAY = 1000;
const tooltipBoxStyle = {
  border: "none",
  borderRadius: "var(--border-radius-md, 4px)",
  backgroundColor: "#000",
  color: "#fff",
  padding: "4px 8px",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "20px",
  boxShadow: "none",
  boxSizing: "border-box",
  whiteSpace: "normal",
  overflowWrap: "break-word",
};

const EllipsisLine = ({ text, children, style = {}, delay = TOOLTIP_OPEN_DELAY }) => {
  const anchorRef = React.useRef(null);
  const showTimerRef = React.useRef(null);
  const [tooltipRect, setTooltipRect] = React.useState(null);

  const clearShowTimer = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  const scheduleTooltip = () => {
    if (showTimerRef.current || tooltipRect || !anchorRef.current) return;

    const isTruncated =
      anchorRef.current.scrollWidth > anchorRef.current.clientWidth;
    if (!isTruncated) return;

    showTimerRef.current = setTimeout(() => {
      const rect = anchorRef.current?.getBoundingClientRect?.();
      if (!rect) return;

      const width = Math.round(rect.width);
      const left = Math.max(8, Math.min(Math.round(rect.left), window.innerWidth - width - 8));

      setTooltipRect({
        left,
        top: Math.round(rect.bottom + 4),
        width,
      });
      showTimerRef.current = null;
    }, delay);
  };

  const handleMouseLeave = () => {
    clearShowTimer();
    setTooltipRect(null);
  };

  React.useEffect(() => () => clearShowTimer(), []);

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={scheduleTooltip}
        onMouseMove={scheduleTooltip}
        onMouseLeave={handleMouseLeave}
        style={{
          display: "block",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...style,
        }}
      >
        {children}
      </div>
      {tooltipRect && text && typeof document !== "undefined" && createPortal(
        <div
          className="matter-search-hover-popup"
          style={{
            ...tooltipBoxStyle,
            position: "fixed",
            left: tooltipRect.left,
            top: tooltipRect.top,
            width: tooltipRect.width,
            maxWidth: tooltipRect.width,
            zIndex: 1000003,
            pointerEvents: "none",
          }}
        >
            {text}
        </div>,
        document.body
      )}
    </>
  );
};

export const HighlightText = ({
  number,
  name,
  secondaryText,
  highlight,
  li,
  minWidth = "100%",
  secondaryColor = "#666",
  enableTooltip = false,
  tooltipDelay = TOOLTIP_OPEN_DELAY,
}) => {
  if (!React.isValidElement(li)) return li;

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text) => {
    if (!highlight || typeof text !== "string") return text;

    const regex = new RegExp(`(${escapeRegExp(highlight)})`, "ig");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      index % 2 === 1 ? (
        <span
          key={index}
          className="highlighted-keyword"
        >
          {part}
        </span>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
  };

  const renderSingleLine = (text, children, style = {}) => {
    if (!enableTooltip) {
      return (
        <div
          style={{
            display: "block",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ...style,
          }}
        >
          {children}
        </div>
      );
    }

    return (
      <EllipsisLine text={text} style={style} delay={tooltipDelay}>
        {children}
      </EllipsisLine>
    );
  };

  const mainText = [number, name].filter(Boolean).join(" ");
  const mainContent = renderSingleLine(
    mainText,
    <>
      {number && <strong className="font-bold">{highlightText(number)}</strong>}
      {number && " "}
      {name && highlightText(name)}
    </>
  );

  const content = secondaryText ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, width: "100%", lineHeight: "20px" }}>
      {renderSingleLine(
        mainText,
        <>
        {number && <strong className="font-bold">{highlightText(number)}</strong>}
        {number && " "}
        {name && highlightText(name)}
        </>
      )}
      {renderSingleLine(
        secondaryText,
        highlightText(secondaryText),
        { color: secondaryColor }
      )}
    </div>
  ) : mainContent;

  return React.cloneElement(
    li,
    {
      className: (li.props.className || "") + " has-highlight",
      style: { ...li.props.style, width: "100%", maxWidth: minWidth, minWidth: 0, minHeight: secondaryText ? 48 : undefined },
    },
    content
  );
};
